import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellRing } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAdminNotifications } from '../hooks/useAdminNotifications';
import NotificationsPanel from './NotificationsPanel';

interface NotificationsButtonProps {
  onOpen?: () => void;
  onClose?: () => void;
}

const NotificationsButton: React.FC<NotificationsButtonProps> = ({ onOpen, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNotificationBadge, setShowNotificationBadge] = useState(false);
  const { notifications, unreadCount, fetchNotifications } = useAdminNotifications();
  const navigate = useNavigate();
  const previousNotificationsLength = useRef(notifications.length);
  const updateInterval = useRef<NodeJS.Timeout | null>(null);

  // Log para depuração
  useEffect(() => {
    console.log('NotificationsButton - Estado atual:', {
      isOpen,
      showNotificationBadge,
      notificationsLength: notifications.length,
      unreadCount,
      previousLength: previousNotificationsLength.current
    });
  }, [isOpen, showNotificationBadge, notifications, unreadCount]);

  // Efeito para mostrar animação apenas quando uma nova notificação é criada
  useEffect(() => {
    if (notifications.length > previousNotificationsLength.current) {
      console.log('Nova notificação detectada:', {
        atual: notifications.length,
        anterior: previousNotificationsLength.current
      });
      setShowNotificationBadge(true);
      const timer = setTimeout(() => setShowNotificationBadge(false), 5000);
      return () => clearTimeout(timer);
    }
    previousNotificationsLength.current = notifications.length;
  }, [notifications.length]);

  // Atualizar notificações apenas quando o painel estiver aberto
  useEffect(() => {
    if (isOpen) {
      console.log('Iniciando atualizações automáticas');
      fetchNotifications(); // Buscar imediatamente ao abrir
      updateInterval.current = setInterval(fetchNotifications, 5000);
    }

    return () => {
      if (updateInterval.current) {
        console.log('Parando atualizações automáticas');
        clearInterval(updateInterval.current);
        updateInterval.current = null;
      }
    };
  }, [isOpen, fetchNotifications]);

  const handleOpen = useCallback(() => {
    console.log('Alternando estado do painel');
    setIsOpen(prev => !prev);
    if (!isOpen) {
      onOpen?.();
    } else {
      onClose?.();
    }
  }, [isOpen, onOpen, onClose]);

  const handleClose = useCallback(() => {
    console.log('Fechando painel');
    setIsOpen(false);
    onClose?.();
    if (updateInterval.current) {
      clearInterval(updateInterval.current);
      updateInterval.current = null;
    }
  }, [onClose]);

  const handleNavigateToNotifications = useCallback(() => {
    console.log('Navegando para a página de notificações');
    navigate('/admin/notifications');
    setShowNotificationBadge(false);
  }, [navigate]);

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleOpen}
        className="relative p-2 hover:bg-white/5 rounded-xl transition-colors flex items-center gap-2"
      >
        <div className="relative">
          <AnimatePresence mode="wait">
            {unreadCount > 0 ? (
              <motion.div
                key="bell-ring"
                initial={{ rotate: -30, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 30, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                <BellRing className="w-5 h-5 text-primary" />
              </motion.div>
            ) : (
              <motion.div
                key="bell"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Bell className="w-5 h-5" />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {unreadCount > 0 && (
              <motion.div
                key={unreadCount}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-primary rounded-full flex items-center justify-center px-1"
              >
                <span className="text-[10px] font-medium text-primary-foreground">
                  {unreadCount}
                </span>
                <motion.div
                  className="absolute inset-0 bg-primary rounded-full"
                  initial={{ scale: 1 }}
                  animate={{ scale: 1.5, opacity: 0 }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.button>

      {/* Toast de Nova Notificação */}
      <AnimatePresence>
        {showNotificationBadge && !isOpen && (
          <motion.button
            onClick={handleNavigateToNotifications}
            initial={{ opacity: 0, y: 20, x: "100%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -20, x: "100%" }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            className="fixed bottom-4 right-4 bg-primary/10 backdrop-blur-lg border border-primary/20 rounded-lg p-4 shadow-lg z-50 hover:bg-primary/20 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/20 rounded-full">
                <BellRing className="w-4 h-4 text-primary" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium">Nova Notificação</p>
                <p className="text-xs text-gray-400">Clique para ver mais detalhes</p>
              </div>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Overlay para fechar quando clicar fora */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40"
            onClick={handleClose}
          />
        )}
      </AnimatePresence>

      {/* Painel de Notificações */}
      <div className="absolute right-0 top-full mt-2">
        <NotificationsPanel isOpen={isOpen} onClose={handleClose} />
      </div>
    </>
  );
};

export default NotificationsButton; 